package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * `/app/me/schedule` — 수강 시간표와 단발 일정. 일정 계약이 연결되기 전에도 월 탐색·선택일
 * 목록의 화면 구조를 제공한다. 정기 수업은 이벤트가 아니므로 아래 별도 시간표에만 둔다.
 */
object MySchedulePage {
    private val scope = MainScope()

    /** 시간표 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "member-schedule-retry"
    private var calendarState = CalendarState.current()

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            MemberShell.render(context, MemberShell.Tab.SCHEDULE, body(context))
            wireRetry(RETRY_ID) { render(session) }
            wireCalendar()
        }
    }

    private suspend fun body(context: MemberContext): String {
        val academyId = context.subjectAcademyId()
        val studentUserId = context.subjectStudentUserId()
        val scheduleResult = if (academyId != null && studentUserId != null) {
            context.repository.studentScheduleResult(academyId, studentUserId)
        } else {
            ReadResult()
        }
        val classes = scheduleResult.items

        val rows = if (classes.isEmpty()) {
            // 시간표를 못 읽은 것을 "등록된 수업이 없어요"로 그리면 학생이 수업이 없는 줄 안다(#30).
            val failure = scheduleResult.failure
            if (failure != null) {
                readErrorHtml(failure, RETRY_ID)
            } else {
                """<p class="eb-meta">${if (academyId == null) "학원 초대를 수락하면 수강 시간표가 여기에 나타나요." else "등록된 수업이 없어요."}</p>"""
            }
        } else {
            classes
                .sortedWith(compareBy({ it.schedule.daysOfWeek.minOrNull() ?: 8 }, { it.schedule.startMinuteOfDay }))
                .joinToString("") { classData ->
                """
                <div class="eb-list-row">
                    ${icon("icon-calendar", "md")}
                    <span>${classData.schedule.displayLabel.escapeHtml()} · ${classData.className.escapeHtml()} (${classData.subject.escapeHtml()})</span>
                </div>
                """.trimIndent()
            }
        }

        return """
            <h1 class="eb-page-title">일정</h1>
            <section class="eb-card eb-schedule-calendar" id="member-schedule-calendar" aria-label="일정 달력">
                ${calendarHtml()}
            </section>
            <section class="eb-card eb-quick-links" aria-labelledby="member-class-schedule-title">
                <h2 id="member-class-schedule-title">수강 시간표</h2>
                <p class="eb-meta">정기 수업은 일정과 별도로 관리됩니다.</p>
                $rows
            </section>
        """.trimIndent()
    }

    private fun wireCalendar() {
        (document.getElementById("member-schedule-calendar-prev") as? HTMLButtonElement)?.onclick = {
            calendarState = calendarState.withMonthOffset(-1)
            refreshCalendar("member-schedule-calendar-prev")
            null
        }
        (document.getElementById("member-schedule-calendar-next") as? HTMLButtonElement)?.onclick = {
            calendarState = calendarState.withMonthOffset(1)
            refreshCalendar("member-schedule-calendar-next")
            null
        }

        val dayButtons = document.querySelectorAll(".member-schedule-calendar-day")
        for (index in 0 until dayButtons.length) {
            val button = dayButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                button.getAttribute("data-service-date")?.let { serviceDate ->
                    calendarState = calendarState.withSelectedDate(serviceDate)
                    refreshCalendar("member-schedule-calendar-day-$serviceDate")
                }
                null
            }
        }
    }

    private fun refreshCalendar(focusTargetId: String) {
        val container = document.getElementById("member-schedule-calendar") as? HTMLElement ?: return
        container.innerHTML = calendarHtml()
        wireCalendar()
        (document.getElementById(focusTargetId) as? HTMLElement)?.focus()
    }

    private fun calendarHtml(): String {
        val month = Date(calendarState.year, calendarState.monthIndex, 1)
        val firstWeekday = (month.getDay() + 6) % 7
        val daysInMonth = Date(calendarState.year, calendarState.monthIndex + 1, 0).getDate()
        val days = buildString {
            repeat(firstWeekday) { append("<span class=\"eb-schedule-calendar__day--empty\" aria-hidden=\"true\"></span>") }
            for (day in 1..daysInMonth) {
                val serviceDate = "${calendarState.year.toString().padStart(4, '0')}-${(calendarState.monthIndex + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
                val selected = serviceDate == calendarState.selectedDate
                val today = serviceDate == todayServiceDate()
                append(
                    """
                    <button class="eb-schedule-calendar__day member-schedule-calendar-day${if (selected) " eb-schedule-calendar__day--selected" else ""}${if (today) " eb-schedule-calendar__day--today" else ""}" id="member-schedule-calendar-day-$serviceDate" type="button" data-service-date="$serviceDate" aria-pressed="$selected" aria-label="${calendarState.monthIndex + 1}월 ${day}일${if (today) ", 오늘" else ""}">
                        <span>$day</span>
                    </button>
                    """.trimIndent(),
                )
            }
        }

        return """
            <div class="eb-schedule-calendar__header">
                <div>
                    <h2>일정 캘린더</h2>
                    <p class="eb-meta">학원 일정과 개인 일정을 날짜별로 확인합니다.</p>
                </div>
                <div class="eb-schedule-calendar__navigation" aria-label="월 이동">
                    <button class="eb-icon-button eb-icon-button--sm" id="member-schedule-calendar-prev" type="button" aria-label="이전 달">‹</button>
                    <strong aria-live="polite">${calendarState.year}년 ${calendarState.monthIndex + 1}월</strong>
                    <button class="eb-icon-button eb-icon-button--sm" id="member-schedule-calendar-next" type="button" aria-label="다음 달">›</button>
                </div>
            </div>
            <ul class="eb-schedule-calendar__legend" aria-label="일정 종류">
                <li class="eb-schedule-calendar__type eb-schedule-calendar__type--academy">${icon("icon-academy", "xs")}<span>학원 일정</span></li>
                <li class="eb-schedule-calendar__type eb-schedule-calendar__type--personal">${icon("icon-person", "xs")}<span>개인 일정</span></li>
            </ul>
            <div class="eb-schedule-calendar__weekdays" aria-hidden="true">
                <span>월</span><span>화</span><span>수</span><span>목</span><span>금</span><span>토</span><span>일</span>
            </div>
            <div class="eb-schedule-calendar__grid" role="group" aria-label="${calendarState.year}년 ${calendarState.monthIndex + 1}월">
                $days
            </div>
            <section class="eb-schedule-calendar__selected" aria-live="polite" aria-labelledby="member-schedule-selected-date">
                <h3 id="member-schedule-selected-date">${formatSelectedDate(calendarState.selectedDate)}</h3>
                <p class="eb-meta">등록된 일정이 없어요. 학원·개인 일정 기능을 연결하는 중입니다.</p>
            </section>
        """.trimIndent()
    }

    private fun formatSelectedDate(serviceDate: String): String {
        val parts = serviceDate.split("-")
        return "${parts[0]}년 ${parts[1].toInt()}월 ${parts[2].toInt()}일"
    }

    private data class CalendarState(
        val year: Int,
        val monthIndex: Int,
        val selectedDate: String,
    ) {
        fun withMonthOffset(offset: Int): CalendarState {
            val target = Date(year, monthIndex + offset, 1)
            return copy(
                year = target.getFullYear(),
                monthIndex = target.getMonth(),
                selectedDate = formatServiceDate(target),
            )
        }

        fun withSelectedDate(serviceDate: String): CalendarState = copy(selectedDate = serviceDate)

        companion object {
            fun current(): CalendarState {
                val now = Date()
                return CalendarState(now.getFullYear(), now.getMonth(), formatServiceDate(now))
            }
        }
    }

}
